/**
 * Default settings values for FrontDesk+ client
 *
 * Used when settings are not present in storage (all platforms).
 */

/**
 * Hard-coded feature flags for FrontDesk+ client
 *
 * These flags control feature availability at build time.
 * To enable a feature, change the value and rebuild/update the app.
 *
 * @since 1.0.0
 */
export const FEATURE_FLAGS = {
  /**
   * Enable eEB (elektronische Ersatzbescheinigung) flow.
   * When false, GKV patients skip quarterly coverage check and go directly to done.
   * Runtime availability still requires the qualified global proof and local TI/KIM readiness.
   */
  eeb: true,

  /**
   * Enable PKV QR code option in card check step.
   * When false, PKV patients use the manual check-in flow instead.
   */
  pkvQrCode: true,

  /**
   * Enable voice-over audio assistant.
   * When false, voice-over volume control is hidden in settings.
   */
  voiceover: true,

  /**
   * Enable prescription self-service flow.
   * When false, the prescription flow card is hidden on the start screen.
   */
  prescriptionService: true,

  /**
   * Show the "Hilfe vom Empfang" reception-help button in the kiosk footer.
   * Hidden for now (2026-06-08) — flip to true to bring it back. The current
   * handler is a placeholder toast; wire it to a real staff-notification RPC
   * before re-enabling.
   */
} as const;

export type FeatureFlags = typeof FEATURE_FLAGS;

export interface PracticeSettings {
  /** Doc-Cirrus bridge server URL */
  bridgeUrl: string;
  /** Practice name for display */
  practiceName: string;
  /** Welcome message shown on start screen */
  welcomeMessage: string;
  /** Selected card reader terminal ID */
  selectedTerminalId?: string;
  /** Device name for identification in bridge dashboard */
  deviceName?: string;
  /** SOLTOKEN for authenticating with inSuite _rest_ proxy */
  solToken?: string;
  /** KIM address override - uses bridge default if not set */
  kimAddressOverride?: string;
  /** Whether scheduler flow should be available in self-service */
  schedulerAvailableInSelfService?: boolean;
  /** Whether prescription flow should be available in self-service */
  prescriptionServiceAvailableInSelfService?: boolean;
  /** Quarterly eGK validation behavior for prescription requests */
  prescriptionRequestQuarterlyCheck?: 'warn' | 'block' | 'skip';
  /** Estimated turnaround time for prescription requests */
  prescriptionRequestEstimatedTime?: string;
  /** Whether the digital anamnesis link/QR is shown after a completed check-in */
  anamnesisEnabled?: boolean;
  /** URL to the external digital anamnesis provider (static, identical for all patients) */
  anamnesisUrl?: string;
}

export interface StylingSettings {
  /** PIN for exiting fullscreen/kiosk mode */
  pin: string;
  /** UI language */
  language: "de" | "en";
  /** Voice-over assistant volume (0-100) */
  voiceOverVolume: number;
  /** Admin-configured default kiosk language for fresh patient sessions */
  defaultKioskLanguage?: string;
  /** Offer language selection before the service menu for each fresh session. */
  languageSelectionOnSessionStart?: boolean;
}

export interface WindowState {
  isFullscreen: boolean;
  isKiosk: boolean;
}

export const DEFAULT_PRACTICE_SETTINGS: PracticeSettings = {
  bridgeUrl: "",
  practiceName: "",
  // Empty so the localized `self-service.start.welcome` fallback wins; a German
  // literal here greets a Turkish or Arabic session in German.
  welcomeMessage: "",
  selectedTerminalId: undefined,
  deviceName: undefined,
  kimAddressOverride: undefined,
  schedulerAvailableInSelfService: true,
  prescriptionServiceAvailableInSelfService: true,
  prescriptionRequestQuarterlyCheck: 'warn',
  // Unset by default: a turnaround estimate is a promise about when the
  // practice will be finished, and no default can be truthful for a practice
  // that never configured one.
  prescriptionRequestEstimatedTime: undefined,
  anamnesisEnabled: false,
  anamnesisUrl: undefined,
};

export const DEFAULT_STYLING_SETTINGS: StylingSettings = {
  // Empty means "no PIN configured". The forbidden default `0000` is never a
  // usable secret: an unconfigured device is routed into forced PIN setup
  // rather than being unlocked by a well-known value (ITSiRL §75b SGB V).
  pin: "",
  language: "de",
  voiceOverVolume: 50,
  defaultKioskLanguage: "de",
  languageSelectionOnSessionStart: false,
};

export const DEFAULT_WINDOW_STATE: WindowState = {
  isFullscreen: false,
  isKiosk: false,
};
